import React, { useState } from 'react';
import knowledgeData from '../data/knowledge-base.json';
import penaltyData from '../data/penalty-schedule.json';
import { getAllGovernmentDocuments } from '../utils/governmentDocumentLookup';
import {
  X,
  BookOpen,
  Scale,
  ShieldAlert,
  Users,
  Cpu,
  Search,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ExternalLink,
  Layers,
  Sparkles,
  Landmark,
  FileCheck,
  ShieldCheck,
  Settings,
  ArrowRight,
  Database,
  Workflow
} from 'lucide-react';

interface KnowledgeBaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'exemptions' | 'actors' | 'systems' | 'penalties' | 'government-documents';
  onOpenSettings?: (tab?: string) => void;
}

export default function KnowledgeBaseModal({
  isOpen,
  onClose,
  defaultTab = 'exemptions',
  onOpenSettings
}: KnowledgeBaseModalProps) {
  if (!isOpen) return null;

  const [activeTab, setActiveTab] = useState<'exemptions' | 'actors' | 'systems' | 'penalties' | 'government-documents'>(defaultTab);
  const [search, setSearch] = useState('');

  const governmentDocs = getAllGovernmentDocuments();

  const filteredExemptions = knowledgeData.exemptions.filter(
    (e) =>
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      e.clause.toLowerCase().includes(search.toLowerCase()) ||
      e.summary.toLowerCase().includes(search.toLowerCase()) ||
      e.conditions.toLowerCase().includes(search.toLowerCase())
  );

  const filteredActors = knowledgeData.actors.filter(
    (a) =>
      a.role.toLowerCase().includes(search.toLowerCase()) ||
      a.definition.toLowerCase().includes(search.toLowerCase()) ||
      a.statutory_ref.toLowerCase().includes(search.toLowerCase())
  );

  const filteredSystems = knowledgeData.systems.filter(
    (s) =>
      s.id.toLowerCase().includes(search.toLowerCase()) ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.purpose.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGovDocs = governmentDocs.filter(
    (d) =>
      d.short_title.toLowerCase().includes(search.toLowerCase()) ||
      d.official_title.toLowerCase().includes(search.toLowerCase()) ||
      d.issuer.toLowerCase().includes(search.toLowerCase()) ||
      (d.gazette_notification || '').toLowerCase().includes(search.toLowerCase()) ||
      d.description.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    {
      id: 'exemptions' as const,
      label: 'Statutory Exemptions (s.17 & s.3)',
      shortTitle: 'Statutory Exemptions',
      subtitle: 'Section 17 Safe Harbours & Section 3 Scope Exclusions',
      icon: Scale,
      badge: '5 Safe Harbours',
      count: filteredExemptions.length
    },
    {
      id: 'government-documents' as const,
      label: 'Government Gazette Publications',
      shortTitle: 'Government Gazette',
      subtitle: 'Official Published Notifications & Cryptographic Baselines',
      icon: Landmark,
      badge: `${governmentDocs.length} Baselines`,
      count: filteredGovDocs.length
    },
    {
      id: 'actors' as const,
      label: 'Actor Roles & Compliance Thresholds',
      shortTitle: 'Actor Roles',
      subtitle: 'Statutory Taxonomy & Baseline Duties',
      icon: Users,
      badge: 'Legal Taxonomy',
      count: filteredActors.length
    },
    {
      id: 'systems' as const,
      label: 'Architecture Systems Taxonomy',
      shortTitle: 'Architecture Systems',
      subtitle: 'Enterprise Systems & Application Blueprint Reference',
      icon: Cpu,
      badge: 'Architecture',
      count: filteredSystems.length
    },
    {
      id: 'penalties' as const,
      label: 'Penalties Schedule (s.33)',
      shortTitle: 'Penalties Schedule',
      subtitle: 'Maximum Statutory Liability Limits & Fines',
      icon: ShieldAlert,
      badge: '₹250 Cr Max',
      count: penaltyData.length
    }
  ];

  const currentTabInfo = TABS.find((t) => t.id === activeTab) || TABS[0];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-xs">
      <div className="relative w-full max-w-5xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col h-[88vh] max-h-[88vh]">
        
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-900/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-100 tracking-tight">
                  In-App DPDP Statutory & Architecture Knowledge Base
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-accent/10 text-accent dark:text-accent-light border border-accent/20">
                  Self-Contained
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 hidden sm:block">
                Complete legal exemptions, government gazette publications, actor taxonomy, and runtime architecture reference
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body: Left Vertical Icon Rail + Right Content Area */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          
          {/* Vertical Tabs: Icon Only Triggers + Tooltips on Hover */}
          <aside className="w-16 sm:w-18 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-950/60 py-4 flex flex-col items-center gap-3 overflow-y-auto z-20">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <div key={tab.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all cursor-pointer relative ${
                      isActive
                        ? 'bg-accent text-white shadow-lg shadow-accent/30 ring-2 ring-accent/30'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/80 dark:hover:bg-slate-800/90'
                    }`}
                    aria-label={tab.label}
                  >
                    <tab.icon className="w-5 h-5" />
                    
                    {/* Subtle active indicator bar */}
                    {isActive && (
                      <span className="absolute -left-2.5 sm:-left-3 top-1/2 -translate-y-1/2 w-1.5 h-6 bg-accent rounded-r-full" />
                    )}
                  </button>

                  {/* Floating Tooltip with Text on Hover */}
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 hidden group-hover:flex items-center">
                    <div className="bg-slate-900 dark:bg-slate-800 text-white text-xs font-semibold px-3 py-2 rounded-xl shadow-2xl whitespace-nowrap border border-slate-700/80 flex items-center gap-2.5 animate-in fade-in zoom-in-95 duration-100">
                      <div className="flex flex-col text-left">
                        <span className="font-bold text-slate-100">{tab.label}</span>
                        <span className="text-[10px] text-slate-400 font-normal">{tab.subtitle}</span>
                      </div>
                      {tab.badge && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-accent/20 text-accent-light border border-accent/30">
                          {tab.badge}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </aside>

          {/* Right Main Content Pane */}
          <main className="flex-1 flex flex-col min-w-0 bg-white dark:bg-slate-900 overflow-hidden">
            
            {/* Active Tab Toolbar & Search Bar */}
            <div className="px-6 py-3.5 border-b border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-accent/10 dark:bg-accent/20 text-accent flex items-center justify-center shrink-0">
                  <currentTabInfo.icon className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                      {currentTabInfo.label}
                    </h4>
                    <span className="px-1.5 py-0.5 rounded-md text-[10px] font-mono font-bold bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      {currentTabInfo.count} {currentTabInfo.count === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {currentTabInfo.subtitle}
                  </p>
                </div>
              </div>

              {/* Search Filter */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter knowledge items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl pl-8 pr-3 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:border-accent w-full sm:w-56 shadow-xs"
                />
              </div>
            </div>

            {/* Scrollable Content Body */}
            <div className="p-6 overflow-y-auto space-y-4 flex-1">
          
          {/* TAB 1: EXEMPTIONS */}
          {activeTab === 'exemptions' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {filteredExemptions.map((ex) => (
                <div
                  key={ex.id}
                  className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-accent/10 dark:bg-accent/20 text-accent dark:text-accent-light border border-accent/20">
                      {ex.clause}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500">
                      {ex.scope}
                    </span>
                  </div>

                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                    {ex.title}
                  </h4>

                  <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                    {ex.summary}
                  </p>

                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400">
                    <strong className="text-slate-700 dark:text-slate-300">Statutory Limit: </strong>
                    {ex.conditions}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TAB: GOVERNMENT PUBLISHED DOCUMENTS & GAZETTE ARCHIVE */}
          {activeTab === 'government-documents' && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-accent" />
                    <span className="text-xs font-extrabold text-slate-900 dark:text-slate-100">
                      Authoritative Government of India Legal Baseline (15 September 2026)
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                    All control matrix obligations and statutory rules in this register are directly grounded in these published Gazette Notifications, Ministry Orders, and Regulator Directives.
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-1.5 text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-white dark:bg-slate-900 px-3 py-1.5 rounded-xl border border-emerald-500/30">
                  <ShieldCheck className="w-4 h-4" />
                  <span>SHA-256 Verified</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3.5">
                {filteredGovDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-extrabold bg-accent/15 text-accent dark:text-accent-light border border-accent/30">
                            {doc.id}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                            {doc.category_label}
                          </span>
                          {doc.gazette_notification && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60">
                              {doc.gazette_notification}
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100 pt-0.5">
                          {doc.official_title}
                        </h4>

                        <div className="text-xs text-slate-500 dark:text-slate-400 flex flex-wrap items-center gap-3 pt-0.5">
                          <span><strong>Issuer:</strong> {doc.issuer}</span>
                          <span>•</span>
                          <span><strong>Instrument Date:</strong> {doc.publication_date_display}</span>
                        </div>
                      </div>

                      <a
                        href={doc.official_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors shadow-xs shrink-0 self-start sm:self-auto"
                      >
                        <span>Open Official PDF</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-800">
                      {doc.description}
                    </p>

                    <div className="space-y-1.5">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Key Sections, Rules & Controls Derived:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {doc.key_provisions_covered.map((prov, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-lg text-[11px] bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-800"
                          >
                            {prov}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="pt-1 flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-slate-400 dark:text-slate-500 font-mono border-t border-slate-200/60 dark:border-slate-800/80 gap-1">
                      <span className="truncate" title={`Official SHA-256: ${doc.sha256}`}>
                        SHA-256: {doc.sha256}
                      </span>
                      <span>Primary Legal Authority</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 2: ACTOR ROLES */}
          {activeTab === 'actors' && (
            <div className="space-y-4">
              {filteredActors.map((actor) => (
                <div
                  key={actor.role}
                  className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-slate-100">
                        {actor.role}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                        {actor.statutory_ref}
                      </span>
                    </div>
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30">
                      {actor.max_penalty}
                    </span>
                  </div>

                  <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900 p-3 rounded-xl border border-slate-200/80 dark:border-slate-800">
                    {actor.definition}
                  </p>

                  <div className="space-y-1.5">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Core Statutory Duties & Compliance Floor:
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                      {actor.key_duties.map((duty, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{duty}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TAB 3: TARGET ARCHITECTURE SYSTEMS */}
          {activeTab === 'systems' && (
            <div className="space-y-4">
              
              {/* Architecture Mapping Methodology Card */}
              <div className="p-5 rounded-2xl bg-gradient-to-r from-indigo-50/90 via-slate-50 to-purple-50/60 dark:from-indigo-950/40 dark:via-slate-800/60 dark:to-purple-950/30 border border-indigo-200/80 dark:border-indigo-900/60 space-y-3.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-indigo-100 dark:border-indigo-900/40 pb-3">
                  <div className="space-y-1">
                    <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                      <Cpu className="w-4.5 h-4.5 text-indigo-600 dark:text-indigo-400" />
                      <span>How to Map Enterprise Systems to DPDP Obligations</span>
                    </h4>
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                      DPDP Act Section 8(5) & Section 12; Draft Rules Rule 6
                    </span>
                  </div>

                  {onOpenSettings && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        onOpenSettings('systems');
                      }}
                      className="h-8 px-3 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1.5 cursor-pointer shadow-xs transition-colors self-start sm:self-auto shrink-0"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      <span>Register Custom Systems</span>
                    </button>
                  )}
                </div>

                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  Under DPDP Act Section 8(5), enterprises must implement reasonable technical and organizational security safeguards across all digital personal data processing components. Register your organization's custom IT stack in <strong>Settings → Architecture Systems</strong>, then associate the relevant systems to each operational control inside the <strong>Inspect & Scope Drawer</strong> under <em>Systems in Scope</em>.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5 text-xs pt-1">
                  <div className="p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 space-y-1">
                    <span className="font-bold text-indigo-700 dark:text-indigo-300 text-[11px] block">1. Register Inventory</span>
                    <p className="text-[11px] text-slate-500 leading-normal">Add backend services, DBs, IdPs, and SaaS vendors in Settings.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 space-y-1">
                    <span className="font-bold text-indigo-700 dark:text-indigo-300 text-[11px] block">2. Trace Data Flows</span>
                    <p className="text-[11px] text-slate-500 leading-normal">Identify ingestion points, auth flows, and persistent data stores.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 space-y-1">
                    <span className="font-bold text-indigo-700 dark:text-indigo-300 text-[11px] block">3. Tag in Drawer</span>
                    <p className="text-[11px] text-slate-500 leading-normal">Open Inspect on each control and select 'Systems in Scope'.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 space-y-1">
                    <span className="font-bold text-indigo-700 dark:text-indigo-300 text-[11px] block">4. Filter & Verify</span>
                    <p className="text-[11px] text-slate-500 leading-normal">Use System Filter on dashboard to audit coverage per technical asset.</p>
                  </div>
                </div>
              </div>

              {/* Grid of System Archetypes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {filteredSystems.map((sys) => (
                  <div
                    key={sys.id}
                    className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-extrabold bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border border-indigo-500/30">
                        {sys.id}
                      </span>
                      <span className="text-[10px] font-mono text-slate-400">
                        {sys.controls_mapped.length} Controls Mapped
                      </span>
                    </div>

                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-slate-100">
                      {sys.name}
                    </h4>

                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                      {sys.purpose}
                    </p>

                    <div className="flex flex-wrap items-center gap-1 pt-1">
                      {sys.controls_mapped.map((ctrlId) => (
                        <span
                          key={ctrlId}
                          className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400"
                        >
                          {ctrlId}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 4: PENALTIES */}
          {activeTab === 'penalties' && (
            <div className="space-y-3">
              {penaltyData.map((pen) => (
                <div
                  key={pen.id}
                  className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                        {pen.section}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        pen.tier === 'Critical' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400' :
                        pen.tier === 'High' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' :
                        'bg-blue-500/15 text-blue-700 dark:text-blue-400'
                      }`}>
                        {pen.tier} Tier
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                      {pen.description}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="text-base sm:text-lg font-black text-rose-600 dark:text-rose-400 font-mono">
                      {pen.max_penalty_display}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase">Max Statutory Cap</div>
                  </div>
                </div>
              ))}
            </div>
          )}

            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
